// Adapter layer for the Home dashboard.
//
// Wired to medical-api GET /api/v1/medical/home/today (FEATURE_007). The BE returns sections + schedules with
// English CODES + ids; this adapter maps them to the FE DTO the dashboard renders. §2 today-PRO and §3 delay
// are now built (FEATURE_009 / FEATURE_010) — see mapTodayProGroup / mapDelayCard below.

import { httpClient } from "@/api/http-client";
import { endpoints } from "@/api/endpoints";
import { mapAxiosError } from "@/api/error";

// ── Shared types ──

export type GradeLevel = "NORMAL" | "CAUTION" | "WARNING" | "DANGER";

export interface HomePatient {
	uid: string;
	mrn?: string | null;
	name: string;
	primaryDxName: string;
	riskLevel: string | null;
	riskScore: number | null;
	phone: string;
	// v1.4 resting-HR expansion (PRD §4.1): populated by the BE once §1 sends the fields.
	hrAbnormal?: boolean;
	restingHr?: number | null;
}

export interface HomeSeverityPatient extends HomePatient {
	level: GradeLevel;
	score: number | null;
	answerText?: string;
	otherText?: string;
	date: string;
}

export interface HomeTodayItem {
	itemId: string;
	itemName: string;
	area: string;
	cycle: string;
	cycleLabel: string;
	patients: HomeSeverityPatient[];
	dangerCount: number;
	warningCount: number;
}

export interface MissedItem {
	itemId: string;
	itemName: string;
	cycleLabel: string;
	expectedDate: string;
	daysOverdue: number;
}

export interface HomeDelayedPatient extends HomePatient {
	missedItems: MissedItem[];
}

export interface HomeScheduleItem {
	id: string;
	uid: string;
	mrn?: string | null;
	patientName: string;
	time: string;
	type: "visit" | "call" | "fu" | "inperson";
	typeLabel: string;
	purpose: string;
	memo: string;
	assignedStaff: string;
	riskLevel: string | null;
	riskScore: number | null;
	phone: string | null;
	primaryDxName: string;
}

export interface HomeUpcomingScheduleItem extends HomeScheduleItem {
	date: string;
}

export interface HomeDashboardDTO {
	todayStr: string;
	weekStart: string;
	R_DANGER_THRESHOLD: number;
	dangerR: HomePatient[];
	todayItems: HomeTodayItem[];
	severePatientCount: number;
	delayed: HomeDelayedPatient[];
	todaySchedule: HomeScheduleItem[];
	upcomingSchedule: HomeUpcomingScheduleItem[];
}

// ── BE wire shapes (medical-api HomeTodayResponse) ──

interface ApiSuccess<T> {
	data: T;
}
interface HighRiskCardWire {
	uid: string;
	name: string | null;
	mrn: string | null;
	primaryDxName: string | null;
	riskScore: number | null;
	riskLevel: string | null; // G1 | G2 | G3
	// resting-HR expansion (FEATURE_008 High-Risk) — optional until the backend sends these fields.
	hrAbnormal?: boolean | null;
	restingHr?: number | null;
}
interface ScheduleCardWire {
	scheduleId: number;
	scheduledAt: string | null; // ISO local datetime
	type: string | null; // VISIT | INPERSON | CALL | FU
	uid: string;
	patientName: string | null;
	mrn: string | null;
	primaryDxName: string | null;
	riskScore: number | null;
	riskLevel: string | null;
	purpose: string | null;
	memo: string | null;
	assignedStaffId: number | null;
	assignedStaffEmail: string | null;
}
// §2 오늘의 PRO — a group per today-open item + its 단계 3 (G3) responder rows (BE TodayProGroup).
interface TodayProRespondentWire {
	uid: string;
	name: string | null;
	mrn: string | null;
	primaryDxName: string | null;
	riskScore: number | null;
	riskLevel: string | null;
	grade: string | null; // G1 | G2 | G3
	score: number | null;
	level: string | null; // NORMAL | CAUTION | WARNING | DANGER
}
interface TodayProGroupWire {
	itemId: number;
	itemName: string | null;
	cycle: string | null; // DAILY | WEEKLY | BIWEEKLY | MONTHLY | QUARTERLY
	grade3Count: number;
	responderCount: number;
	responders: TodayProRespondentWire[];
}
// §3 PRO 응답 지연 — a card per delayed patient + their unanswered-but-due items (BE DelayCard).
interface DelayMissedItemWire {
	itemId: number;
	itemName: string | null;
	cycle: string | null;
	openedDate: string | null;
	daysOverdue: number;
}
interface DelayCardWire {
	uid: string;
	name: string | null;
	mrn: string | null;
	primaryDxName: string | null;
	riskScore: number | null;
	riskLevel: string | null;
	missedItems: DelayMissedItemWire[];
}
interface HomeTodayWire {
	section1: HighRiskCardWire[];
	section2: TodayProGroupWire[];
	section3: DelayCardWire[];
	todaySchedule: ScheduleCardWire[];
	upcomingSchedule: ScheduleCardWire[];
	greeting: string;
	generatedAt: string;
	// The 고위험 boundary as an administrator has it configured. Optional on the wire: an older backend that
	// predates this field simply omits it, and the caption then falls back rather than printing "undefined".
	highRiskThreshold?: number | null;
}

// PRO schedule cycle wire CODE → Korean 주기 label (Korean-only UI; BE sends the CODE).
const CYCLE_LABEL: Record<string, string> = {
	DAILY: "매일",
	WEEKLY: "매주",
	BIWEEKLY: "2주마다",
	MONTHLY: "매월",
	QUARTERLY: "분기마다",
};
function cycleLabel(code: string | null): string {
	return (code && CYCLE_LABEL[code]) || (code ?? "");
}

// GradeLevel the FE renders — the BE sends the response's own severity level CODE for §2 responders.
// NEVER default an unknown/missing code to DANGER: doing so fabricated the worst reading and made every
// responder show as 단계 3. This maps only recognized codes; the caller filters to the 단계 3 set, so an
// unrecognized value here falls back to WARNING (still the 단계 3 badge) rather than being promoted.
function gradeLevel(code: string | null): GradeLevel {
	return code === "NORMAL" || code === "CAUTION" || code === "WARNING" || code === "DANGER"
		? code
		: "WARNING";
}

// The 고위험 boundary is administrator-configured, so the server sends it and this screen only displays it — a
// constant here would keep printing the old number the moment an administrator edits the band, while the patient
// list beside it already reflects the new one. Kept solely as the fallback for a backend older than that field;
// once every environment serves it this can go (FEATURE_037, cross-app risk-band contract: never hardcode the
// band boundaries). Never compare a score against this to re-derive a band — the server sends `riskLevel`.
const R_DANGER_THRESHOLD_FALLBACK = 14.5;

function mapRiskLevel(code: string | null): string | null {
	switch (code) {
		case "G3":
		case "HIGH":
			return "HIGH";
		case "G2":
		case "MODERATE":
			return "MODERATE";
		case "G1":
		case "LOW":
			return "LOW";
		case "UNKNOWN": // R 산출 불가 (PRD 11 §7.2) — preserve so the FE renders the 산출 불가 chip
			return "UNKNOWN";
		default:
			return null;
	}
}

const SCHEDULE_TYPE_LABEL: Record<string, string> = {
	visit: "외래",
	inperson: "대면",
	call: "전화",
	fu: "재방문",
};

function scheduleType(code: string | null): HomeScheduleItem["type"] {
	const t = (code ?? "").toLowerCase();
	return (t === "visit" || t === "call" || t === "fu" || t === "inperson"
		? t
		: "visit") as HomeScheduleItem["type"];
}

// 담당자 label = the email's local-part (before "@"); prototype renders "담당 {local-part}".
export function staffLabel(email: string | null): string {
	if (!email) return "";
	const at = email.indexOf("@");
	return at > 0 ? email.slice(0, at) : email;
}

function timePart(iso: string | null): string {
	return iso && iso.length >= 16 ? iso.slice(11, 16) : "";
}
function datePart(iso: string | null): string {
	return iso && iso.length >= 10 ? iso.slice(0, 10) : "";
}

function mapSchedule(w: ScheduleCardWire): HomeScheduleItem {
	const type = scheduleType(w.type);
	return {
		id: String(w.scheduleId),
		uid: w.uid,
		mrn: w.mrn,
		patientName: w.patientName ?? "",
		time: timePart(w.scheduledAt),
		type,
		typeLabel: SCHEDULE_TYPE_LABEL[type] ?? type,
		purpose: w.purpose ?? "",
		memo: w.memo ?? "",
		// 담당자 shows the owning staff's email local-part (before "@"), per prototype home.html
		// ("담당 {email-local-part}", e.g. doctor@uuh.ulsan.kr → "doctor").
		assignedStaff: staffLabel(w.assignedStaffEmail),
		riskLevel: mapRiskLevel(w.riskLevel),
		riskScore: w.riskScore,
		phone: null,
		primaryDxName: w.primaryDxName ?? "",
	};
}

/** `YYYY-MM-DD` from a Date's LOCAL fields — never `toISOString()`, see mondayOf. */
function localDatePart(d: Date): string {
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${d.getFullYear()}-${m}-${day}`;
}

// PRD 02 §4.1 weekStart = "이번 주 월요일" (PRD 03 §2: 월요일 00:00 ~ 일요일 24:00, 기관 표준시).
//
// SHCT-827 — the Monday itself was always right; PRINTING it was not. `dateStr + "T00:00:00"`
// parses as LOCAL midnight, and `toISOString()` then converts to UTC, which in KST (+9) lands on
// 15:00 of the PREVIOUS day — so the panel labelled the week 2026-08-02 (Sunday) instead of
// 2026-08-03. Formatting from the local fields keeps the day the arithmetic produced.
function mondayOf(dateStr: string): string {
	const d = new Date(dateStr + "T00:00:00");
	const day = d.getDay(); // 0=Sun..6=Sat
	const diff = day === 0 ? 6 : day - 1;
	d.setDate(d.getDate() - diff);
	return localDatePart(d);
}

// §2 BE TodayProGroup → FE HomeTodayItem (a group with its 단계 3 responder rows).
//
// The card lists ONLY 단계 3 responders (contract §71: severePatientCount = "G3(WARNING+DANGER) 등급 환자").
// The wire carries every responder's grade + a SEPARATE grade3Count, so `responders[]` is not necessarily
// pre-filtered — filter here to the 단계 3 set rather than showing/counting every responder as 단계 3
// (the bug: a freshly-registered patient with no G3 response was still listed as 단계 3). A responder is
// 단계 3 when the wire says so by EITHER signal — grade code G3, or severity level WARNING/DANGER.
function mapTodayProGroup(g: TodayProGroupWire, todayStr: string): HomeTodayItem {
	const patients: HomeSeverityPatient[] = g.responders
		.filter(
			(rp) =>
				rp.grade === "G3" || rp.level === "WARNING" || rp.level === "DANGER",
		)
		.map((rp) => ({
			uid: rp.uid,
			mrn: rp.mrn,
			name: rp.name ?? "",
			primaryDxName: rp.primaryDxName ?? "",
			riskLevel: mapRiskLevel(rp.riskLevel),
			riskScore: rp.riskScore,
			phone: "",
			level: gradeLevel(rp.level),
			score: rp.score,
			date: todayStr,
		}));
	return {
		itemId: String(g.itemId),
		itemName: g.itemName ?? "",
		area: "",
		cycle: g.cycle ?? "",
		cycleLabel: cycleLabel(g.cycle),
		patients,
		// Counted from the levels actually listed, not assumed. The filter above admits a WARNING
		// responder as well as a DANGER one, so the old `dangerCount: patients.length` / `warningCount: 0`
		// pair claimed something the filter beside it contradicted — and the panel reads these two
		// levels apart (PRD 02 §2.4 ②: `modal-pat--danger` / `modal-pat--warning`) even though both
		// wear the same 단계 3 chip.
		dangerCount: patients.filter((p) => p.level === "DANGER").length,
		warningCount: patients.filter((p) => p.level === "WARNING").length,
	};
}

// §3 BE DelayCard → FE HomeDelayedPatient (a patient + their unanswered-but-due items).
function mapDelayCard(c: DelayCardWire): HomeDelayedPatient {
	const missedItems: MissedItem[] = c.missedItems.map((m) => ({
		itemId: String(m.itemId),
		itemName: m.itemName ?? "",
		cycleLabel: cycleLabel(m.cycle),
		expectedDate: m.openedDate ?? "",
		daysOverdue: m.daysOverdue,
	}));
	return {
		uid: c.uid,
		mrn: c.mrn,
		name: c.name ?? "",
		primaryDxName: c.primaryDxName ?? "",
		riskLevel: mapRiskLevel(c.riskLevel),
		riskScore: c.riskScore,
		phone: "",
		missedItems,
	};
}

export async function fetchHomeDashboard(): Promise<HomeDashboardDTO> {
	try {
		const { data } = await httpClient.get<ApiSuccess<HomeTodayWire>>(
			endpoints.homeToday,
		);
		const r = data.data;
		// Fallback is the LOCAL date for the same reason as mondayOf (SHCT-827): before 09:00 KST
		// `new Date().toISOString()` still reads yesterday in UTC, which would date the whole panel
		// — and the week it derives — one day back.
		const todayStr = datePart(r.generatedAt) || localDatePart(new Date());
		const dangerR: HomePatient[] = r.section1.map((c) => ({
			uid: c.uid,
			mrn: c.mrn,
			name: c.name ?? "",
			primaryDxName: c.primaryDxName ?? "",
			riskLevel: mapRiskLevel(c.riskLevel),
			riskScore: c.riskScore,
			phone: "",
			hrAbnormal: c.hrAbnormal ?? false,
			restingHr: c.restingHr ?? null,
		}));
		const todaySchedule = r.todaySchedule.map(mapSchedule);
		const upcomingSchedule: HomeUpcomingScheduleItem[] = r.upcomingSchedule.map(
			(w) => ({ ...mapSchedule(w), date: datePart(w.scheduledAt) }),
		);
		const todayItems = r.section2.map((g) => mapTodayProGroup(g, todayStr));
		// §2 sub-count "단계 3 이상 환자 N명" = the sum of every group's 단계 3 responders.
		const severePatientCount = todayItems.reduce((s, it) => s + it.patients.length, 0);
		const delayed = r.section3.map(mapDelayCard);
		return {
			todayStr,
			weekStart: mondayOf(todayStr),
			// Prefer the served, administrator-configured boundary; fall back only when the backend omits it.
			// `??` (not `||`) on purpose — a configured 0 is a real boundary, and `||` would discard it.
			R_DANGER_THRESHOLD: r.highRiskThreshold ?? R_DANGER_THRESHOLD_FALLBACK,
			dangerR,
			todayItems,
			severePatientCount,
			delayed,
			todaySchedule,
			upcomingSchedule,
		};
	} catch (err) {
		throw mapAxiosError(err);
	}
}
